import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, ErrorState, Skeleton, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useFormatters, type BillingOverview, type BillingPlan } from "../shared";
import { asPlanKey, planPrice, taxOn, type BillingInterval } from "./billing-model";
import { useHandleOrder } from "./checkout";
import { GatewayChooser, useGatewayChoice, type GatewayChoiceState } from "./gateway-chooser";
import { usePlanLimits } from "./plan-limits";
import { TestModeNotice } from "./test-mode-notice";
import { usePlanPreview } from "./use-billing";

export interface PlanChangeTarget {
  plan: BillingPlan;
  interval: BillingInterval;
}

interface PlanChangeDialogProps {
  target: PlanChangeTarget;
  overview: BillingOverview;
  onClose: () => void;
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-fg-muted">{label}</span>
      <span className={strong ? "font-bold tabular-nums" : "font-semibold tabular-nums"}>{value}</span>
    </div>
  );
}

/** A saved method of a gateway that collects the period currency, else the hosted checkout. */
function useChargedTo(overview: BillingOverview, choice: GatewayChoiceState): string {
  const { t } = useTranslation();
  const usable = overview.payment_methods.filter((m) => overview.gateways.includes(m.provider));
  const method = usable.find((m) => m.is_default) ?? usable[0];
  if (!method) return t(`billing.change.hostedCheckout_${choice.gateway ?? "paymongo"}`);
  return t(`billing.methods.label_${method.type}`, { brand: method.brand ?? "", last4: method.last4 ?? "" });
}

/** Same interval: proration preview → `subscription/change`. New interval: hosted checkout. */
export function PlanChangeDialog({ target, overview, onClose }: PlanChangeDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const fmt = useFormatters();
  const message = useErrorMessage();
  const handleOrder = useHandleOrder();
  const limits = usePlanLimits();
  const choice = useGatewayChoice(overview.offered_gateways);
  const chargedTo = useChargedTo(overview, choice);
  const currency = target.plan.currency;
  const tax = overview.region.tax;
  const money = (amount: string | number) => fmt.money(amount, currency);
  const intervalChange = target.interval !== (overview.interval === "annual" ? "annual" : "monthly");
  const planKey = asPlanKey(target.plan.key);
  const preview = usePlanPreview(planKey, target.interval);
  // A new interval alone is a hosted checkout; with an upgrade it restarts the period now, crediting the unused time.
  const restartsPeriod = intervalChange && preview.data?.restart === true;
  const viaPlanChange = !intervalChange || restartsPeriod;
  const price = planPrice(target.plan, target.interval) ?? 0;
  const downgrade = !intervalChange && preview.data?.direction === "downgrade";
  const confirm = useMutation({
    mutationFn: () => {
      if (!planKey) throw new Error("custom plans are quoted by sales");
      return viaPlanChange
        ? unwrap(api.POST("/v1/billing/subscription/change", { body: { plan_key: planKey, interval: target.interval, ...choice.body } }))
        : unwrap(api.POST("/v1/billing/checkout", { body: { plan_key: planKey, interval: target.interval, ...choice.body } }));
    },
    onSuccess: (order) => {
      onClose();
      const success = downgrade
        ? t("billing.change.scheduledToast", { plan: target.plan.name, date: fmt.date(preview.data?.effective_at) })
        : t("billing.change.upgradedToast", { plan: target.plan.name });
      handleOrder(order, success);
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const taxed = preview.data ? taxOn(Number(preview.data.charge), tax) > 0 : false;
  // `trial` is the workspace's real state; `restart` only says the period starts over (a trial has no paid time to prorate).
  const noteKind = preview.data?.trial ? "trial" : preview.data?.restart ? "restart" : "proration";
  const noteKey = `billing.change.${noteKind}Note${taxed ? "" : "NoTax"}` as const;
  const titleKey = intervalChange ? "billing.change.titleInterval" : downgrade ? "billing.change.titleDowngrade" : "billing.change.titleUpgrade";
  const per = target.interval === "annual" ? t("billing.perYear") : t("billing.perMonth");
  const body = intervalChange
    ? t("billing.change.intervalBody", { plan: target.plan.name, price: money(price), monthly: money(planPrice(target.plan, "monthly") ?? 0) })
    : downgrade
      ? limits.downgradeImpact(target.plan, overview)
      : t("billing.change.upgradeBody", { limits: limits.summary(target.plan) });
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(titleKey, { plan: target.plan.name })}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              variant={downgrade ? "danger" : "primary"}
              loading={confirm.isPending}
              disabled={!preview.data && !(intervalChange && preview.isError)}
              onClick={() => { confirm.mutate(); }}
            >
              {downgrade ? t("billing.change.confirmDowngrade", { date: fmt.shortDate(preview.data?.effective_at) }) : intervalChange ? t("billing.change.confirmInterval") : t("billing.change.confirmUpgrade")}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="text-fg-muted">{body}</p>
          <div className="space-y-1 rounded-md bg-surface-2 p-3">
            <Row label={t("billing.change.newPrice")} value={`${money(price)} ${per}`} strong />
            {taxOn(price, tax) > 0 ? <Row label={tax.label} value={money(taxOn(price, tax))} /> : null}
            <Row label={t("billing.change.chargedTo")} value={chargedTo} />
          </div>
          {downgrade ? null : <GatewayChooser state={choice} />}
          {downgrade ? null : <TestModeNotice gateways={[choice.gateway]} />}
          {intervalChange && !restartsPeriod && !preview.isLoading ? null : preview.isPending ? (
            <Skeleton className="h-4 w-full" />
          ) : preview.isError ? (
            <ErrorState headingLevel={3} title={t("billing.change.previewError")} onRetry={() => void preview.refetch()} retryLabel={t("common.retry")} />
          ) : downgrade ? (
            <p className="text-xs text-fg-subtle" data-testid="proration-note">
              {t("billing.change.downgradeNote", { date: fmt.date(preview.data.effective_at), credit: fmt.money(preview.data.credit, preview.data.currency) })}
            </p>
          ) : (
            <p className="text-xs text-fg-subtle" data-testid="proration-note">
              {t(noteKey, {
                amount: fmt.money(preview.data.charge, preview.data.currency),
                tax: fmt.money(taxOn(Number(preview.data.charge), tax), preview.data.currency),
                taxLabel: tax.label,
                plan: target.plan.name,
                days: preview.data.days_remaining,
                credits: fmt.number(preview.data.credits_granted),
                credit: fmt.money(preview.data.credit, preview.data.currency),
              })}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
